/**
 * attendanceHelper.js — pure helpers for the Attendance add-on.
 *
 * Turns raw machine punches (companies/{id}/attendanceLogs) into per-day
 * statuses and a monthly summary per Salary Staff, using the company's rules
 * from companies/{id}/settings/attendance. Nothing here touches payroll math;
 * PayrollRun only consumes `summary.payroll` (present / absent / paidLeave /
 * totalWorkingDays) to pre-fill its existing per-line attendance inputs.
 *
 * Day status codes:
 *   P  Present        L  Late (present, arrived after grace)
 *   H  Half Day       A  Absent
 *   PL Paid Leave     O  Weekly off / holiday
 *   -  Future day / not yet evaluated
 */

import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export const DEFAULT_ATTENDANCE_SETTINGS = {
  shiftStart: '10:00',
  shiftEnd: '19:00',
  graceMinutes: 15,
  weeklyOffDays: [0],                 // 0 = Sunday … 6 = Saturday
  holidays: [],                       // ['2026-09-06', ...]
  lateRule: { enabled: true, latesPerAbsent: 3 },
  halfDay: { enabled: true, minHours: 4 },
  earlyLeave: { enabled: false, minutes: 30 },   // leaving > N min before shift end counts as a "late" for the late rule
  overtime: { enabled: false, afterShiftEnd: true, minMinutes: 30, standardHoursPerDay: 9 }, // OT = time worked past shift end (or past standard hours)
  singlePunchPolicy: 'present',       // 'present' | 'halfDay' | 'absent'
  workingDaysMode: 'calendar',        // 'calendar' (month days − offs − holidays) | 'fixed'
  fixedWorkingDays: 26,
  tzOffsetMinutes: 300,               // Asia/Karachi
};

export const STATUS_META = {
  P:  { label: 'Present',    color: '#2e7d32', bg: '#e8f5e9' },
  L:  { label: 'Late',       color: '#ef6c00', bg: '#fff3e0' },
  H:  { label: 'Half Day',   color: '#6a1b9a', bg: '#f3e5f5' },
  A:  { label: 'Absent',     color: '#c62828', bg: '#ffebee' },
  PL: { label: 'Paid Leave', color: '#1565c0', bg: '#e3f2fd' },
  O:  { label: 'Off',        color: '#757575', bg: '#f5f5f5' },
  '-': { label: '—',         color: '#bdbdbd', bg: 'transparent' },
};

export const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const mergeSettings = (raw) => ({
  ...DEFAULT_ATTENDANCE_SETTINGS,
  ...(raw || {}),
  lateRule: { ...DEFAULT_ATTENDANCE_SETTINGS.lateRule, ...(raw?.lateRule || {}) },
  halfDay: { ...DEFAULT_ATTENDANCE_SETTINGS.halfDay, ...(raw?.halfDay || {}) },
  earlyLeave: { ...DEFAULT_ATTENDANCE_SETTINGS.earlyLeave, ...(raw?.earlyLeave || {}) },
  overtime: { ...DEFAULT_ATTENDANCE_SETTINGS.overtime, ...(raw?.overtime || {}) },
  weeklyOffDays: Array.isArray(raw?.weeklyOffDays) ? raw.weeklyOffDays : DEFAULT_ATTENDANCE_SETTINGS.weeklyOffDays,
  holidays: Array.isArray(raw?.holidays) ? raw.holidays : [],
});

// ─── Firestore loaders ──────────────────────────────────────────────────────

export async function loadAttendanceSettings(companyId) {
  const snap = await getDoc(doc(db, 'companies', companyId, 'settings', 'attendance'));
  return mergeSettings(snap.exists() ? snap.data() : null);
}

/** All punches for a month (YYYY-MM). Single equality filter → no composite index. */
export async function fetchMonthLogs(companyId, month) {
  const snap = await getDocs(query(
    collection(db, 'companies', companyId, 'attendanceLogs'),
    where('month', '==', month)
  ));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Manual overrides for a month. Doc id = `${staffId}_${date}`. */
export async function fetchMonthAdjustments(companyId, month) {
  const snap = await getDocs(query(
    collection(db, 'companies', companyId, 'attendanceAdjustments'),
    where('month', '==', month)
  ));
  const map = {};
  snap.docs.forEach(d => { const a = d.data(); map[`${a.staffId}_${a.date}`] = { id: d.id, ...a }; });
  return map;
}

// ─── Date utils ─────────────────────────────────────────────────────────────

export const pad2 = (n) => String(n).padStart(2, '0');

export function daysInMonth(month) {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m, 0).getDate();
}

export function monthDates(month) {
  const n = daysInMonth(month);
  const out = [];
  for (let d = 1; d <= n; d++) out.push(`${month}-${pad2(d)}`);
  return out;
}

export function weekdayOf(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

export function todayStr() {
  const t = new Date();
  return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}`;
}

const toMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [h, m, s] = String(hhmm).split(':').map(Number);
  return (h || 0) * 60 + (m || 0) + ((s || 0) / 60);
};

// Hours shown as hours + minutes (2026-10-31): 763 min → "12h 43m", 40 → "40m".
// The decimal hours (12.72) stay as they are for the overtime pay sum.
export const fmtHM = (minutes) => {
  if (minutes == null || !Number.isFinite(Number(minutes))) return '—';
  const total = Math.max(0, Math.round(Number(minutes)));
  const h = Math.floor(total / 60), m = total % 60;
  return h > 0 ? `${h}h ${pad2(m)}m` : `${m}m`;
};
// Minutes for display: the exact minutes when known, else worked back from decimal hours.
export const minutesOf = (minutes, hours) => (minutes != null && Number.isFinite(Number(minutes))
  ? Number(minutes)
  : (hours != null && Number.isFinite(Number(hours)) ? Math.round(Number(hours) * 60) : null));

// "18:29:45" -> "6:29 PM"
export const fmt12 = (hhmmss) => {
  if (!hhmmss) return '—';
  const [h, m] = String(hhmmss).split(':').map(Number);
  if (h == null || isNaN(h)) return '—';
  const ap = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ap}`;
};

export function isOffDay(dateStr, settings) {
  if ((settings.holidays || []).includes(dateStr)) return true;
  return (settings.weeklyOffDays || []).includes(weekdayOf(dateStr));
}

// ─── Core: one staff, one day ──────────────────────────────────────────────

/**
 * @param {Array} punches  logs for this staff on this date (any order)
 * @param {object} settings merged settings
 * @param {string} dateStr YYYY-MM-DD
 * @param {object|null} adjustment manual override doc { status: 'P'|'PL'|'A'|'H'|'O', note }
 * @returns {{ status, inTime, outTime, workedHours, lateMinutes, earlyMinutes, punches, isOff, adjusted }}
 */
export function computeDayStatus(punches, settings, dateStr, adjustment) {
  const sorted = [...(punches || [])].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  const inTime = sorted[0]?.time || null;
  const outTime = sorted.length > 1 ? sorted[sorted.length - 1].time : null;
  const isOff = isOffDay(dateStr, settings);
  const future = dateStr > todayStr();

  let workedHours = null;
  // For showing "12h 43m": whole minutes between the In and Out times as shown
  // (8:57 AM → 9:40 PM = 12h 43m), so the figure matches what the user sees.
  let workedMinutes = null;
  const wholeMin = (t) => Math.floor(toMinutes(t));
  if (inTime && outTime) {
    let diff = toMinutes(outTime) - toMinutes(inTime);
    if (diff < 0) diff += 24 * 60;
    workedHours = Math.round((diff / 60) * 100) / 100;
    let shown = wholeMin(outTime) - wholeMin(inTime);
    if (shown < 0) shown += 24 * 60;
    workedMinutes = shown;
  }

  const shiftStart = toMinutes(settings.shiftStart);
  const shiftEnd = toMinutes(settings.shiftEnd);
  const grace = Number(settings.graceMinutes) || 0;
  let lateMinutes = 0, earlyMinutes = 0;
  if (inTime && shiftStart != null) lateMinutes = Math.max(0, Math.round(toMinutes(inTime) - shiftStart));
  if (outTime && shiftEnd != null) earlyMinutes = Math.max(0, Math.round(shiftEnd - toMinutes(outTime)));

  // Overtime: time worked past shift end (or past standard hours), if enabled.
  let otHours = 0;
  let otMinutes = 0;          // for showing "3h 40m" (whole minutes, as the times are shown)
  if (settings.overtime?.enabled && outTime) {
    let otMin = 0, otShown = 0;
    if (settings.overtime.afterShiftEnd !== false && shiftEnd != null) {
      otMin = Math.max(0, toMinutes(outTime) - shiftEnd);
      otShown = Math.max(0, wholeMin(outTime) - Math.floor(shiftEnd));
    } else if (workedHours != null) {
      otMin = Math.max(0, workedHours * 60 - (Number(settings.overtime.standardHoursPerDay) || 0) * 60);
      otShown = Math.max(0, (workedMinutes || 0) - Math.round((Number(settings.overtime.standardHoursPerDay) || 0) * 60));
    }
    if (otMin >= (Number(settings.overtime.minMinutes) || 0)) {
      otHours = Math.round((otMin / 60) * 100) / 100;
      otMinutes = otShown;
    }
  }

  const base = { inTime, outTime, workedHours, workedMinutes, lateMinutes, earlyMinutes, otHours, otMinutes, punches: sorted, isOff, adjusted: !!adjustment };

  if (adjustment?.status) return { ...base, status: adjustment.status };
  if (isOff) return { ...base, status: 'O' };
  if (future) return { ...base, status: '-' };
  if (sorted.length === 0) return { ...base, status: 'A' };

  // Single punch (no out)
  if (sorted.length === 1) {
    const pol = settings.singlePunchPolicy || 'present';
    if (pol === 'absent') return { ...base, status: 'A' };
    if (pol === 'halfDay') return { ...base, status: 'H' };
    return { ...base, status: lateMinutes > grace ? 'L' : 'P' };
  }

  if (settings.halfDay?.enabled && workedHours != null && workedHours < Number(settings.halfDay.minHours || 0)) {
    return { ...base, status: 'H' };
  }
  const isLate = lateMinutes > grace;
  const isEarly = settings.earlyLeave?.enabled && earlyMinutes > Number(settings.earlyLeave.minutes || 0);
  return { ...base, status: (isLate || isEarly) ? 'L' : 'P' };
}

// ─── Monthly summary ───────────────────────────────────────────────────────

/**
 * @param {object} p
 * @param {string} p.month YYYY-MM
 * @param {Array}  p.logs  all attendanceLogs docs for the month
 * @param {Array}  p.staff salaryStaff docs (need id, fullName, staffCode, deviceUserId)
 * @param {object} p.settings merged settings
 * @param {object} p.adjustments map `${staffId}_${date}` → adjustment
 * @returns {{ byStaff: {[staffId]: StaffSummary}, unmapped: {[deviceUserId]: {count, name, lastDate}}, dates: string[] }}
 */
export function buildMonthlySummary({ month, logs, staff, settings, adjustments = {} }) {
  const dates = monthDates(month);
  const today = todayStr();
  const evalDates = dates.filter(d => d <= today);

  // Group logs: by staffId (via stored staffId OR live deviceUserId mapping) → date
  const deviceToStaff = {};
  staff.forEach(s => { if (s.deviceUserId != null && String(s.deviceUserId).trim() !== '') deviceToStaff[String(s.deviceUserId).trim()] = s.id; });

  const grouped = {};   // staffId → date → punches[]
  const unmapped = {};  // deviceUserId → info
  for (const l of logs) {
    const sid = deviceToStaff[String(l.deviceUserId)] || l.staffId || null;
    if (!sid) {
      const u = unmapped[l.deviceUserId] || { count: 0, name: l.deviceUserName || '', lastDate: '' };
      u.count++; if (l.deviceUserName) u.name = l.deviceUserName; if ((l.date || '') > u.lastDate) u.lastDate = l.date;
      unmapped[l.deviceUserId] = u;
      continue;
    }
    if (!grouped[sid]) grouped[sid] = {};
    if (!grouped[sid][l.date]) grouped[sid][l.date] = [];
    grouped[sid][l.date].push(l);
  }

  const workingDaysCalendar = dates.filter(d => !isOffDay(d, settings)).length;
  const totalWorkingDays = settings.workingDaysMode === 'fixed'
    ? (Number(settings.fixedWorkingDays) || workingDaysCalendar)
    : workingDaysCalendar;

  const byStaff = {};
  for (const s of staff) {
    const days = {};
    let present = 0, late = 0, half = 0, absent = 0, paidLeave = 0, off = 0, totalHours = 0, otHoursTotal = 0;
    let totalMinutes = 0, otMinutesTotal = 0;   // exact sums for the hours + minutes display
    for (const d of dates) {
      const adj = adjustments[`${s.id}_${d}`] || null;
      const r = computeDayStatus(grouped[s.id]?.[d] || [], settings, d, adj);
      days[d] = r;
      if (r.workedHours) { totalHours += r.workedHours; totalMinutes += r.workedMinutes || 0; }
      if (r.otHours && (r.status === 'P' || r.status === 'L' || r.status === 'H')) { otHoursTotal += r.otHours; otMinutesTotal += r.otMinutes || 0; }
      switch (r.status) {
        case 'P': present++; break;
        case 'L': present++; late++; break;
        case 'H': half++; break;
        case 'A': absent++; break;
        case 'PL': paidLeave++; break;
        case 'O': off++; break;
        default: break;
      }
    }
    const latesPerAbsent = Number(settings.lateRule?.latesPerAbsent) || 0;
    const lateAbsents = (settings.lateRule?.enabled && latesPerAbsent > 0) ? Math.floor(late / latesPerAbsent) : 0;

    // Payroll mapping (must satisfy present + absent + paidLeave == totalWorkingDays)
    const presentDays = present + half * 0.5;
    let payPresent = Math.max(0, presentDays - lateAbsents);
    let payPaidLeave = paidLeave;
    if (payPresent + payPaidLeave > totalWorkingDays) {
      payPresent = Math.max(0, totalWorkingDays - payPaidLeave);
      if (payPresent + payPaidLeave > totalWorkingDays) payPaidLeave = totalWorkingDays;
    }
    const payAbsent = Math.max(0, Math.round((totalWorkingDays - payPresent - payPaidLeave) * 100) / 100);

    byStaff[s.id] = {
      staffId: s.id, staffName: s.fullName, staffCode: s.staffCode, deviceUserId: s.deviceUserId || '',
      days,
      counts: { present, late, half, absent, paidLeave, off, lateAbsents, evaluatedDays: evalDates.filter(d => !isOffDay(d, settings)).length, totalHours: Math.round(totalHours * 10) / 10, otHours: Math.round(otHoursTotal * 100) / 100, totalMinutes, otMinutes: otMinutesTotal },
      payroll: { totalWorkingDays, present: payPresent, absent: payAbsent, paidLeave: payPaidLeave, otHours: Math.round(otHoursTotal * 100) / 100 },
    };
  }

  return { byStaff, unmapped, dates, totalWorkingDays };
}
